import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Dialog, DialogTitle, ListItemIcon, Menu, MenuItem } from '@mui/material'
import hatch from '../../images/hatch.png'
import NewBookingForm from './NewBookingForm'
import { addHours, startOfDay, addMinutes, format } from 'date-fns'
import { useSelector } from 'react-redux'
import EditBookingForm from './EditBookingForm'
import { Create, Delete, Send } from '@mui/icons-material'
import { AVAILABILITY_COLLECTION, BOOKING_COLLECTION, BOOKING_TYPE_ACTIVITY, BOOKING_TYPE_MAINTENANCE, BOOKING_TYPE_RENTING } from '../../_constants/globals'
import { useNavigate } from 'react-router-dom'
import { ACTIVITIES } from '../../_constants/routes'
import { useFirestore } from '../../hooks/useFirestore'
import useAuth from '../../hooks/useAuth'
import { pickBy } from 'lodash'
import NewFlightDialog from '../flights/NewFlightDialog'
import WriteAvailabilityDialog from './WriteAvailabilityDialog'


const Slot = ({ id, start, length, slotType, resource, booking, day, label }) => {
  
  const profile = useAuth().getProfile()
  const navigate = useNavigate()
  const adminMode = useSelector(state => state.data.adminMode)
  const availabilityHooks = useFirestore(AVAILABILITY_COLLECTION, { storeAs: 'dailyAvailabilities' })
  const availabilities = availabilityHooks.getDocs()
  const bookingHooks = useFirestore(BOOKING_COLLECTION, { storeAs: 'PlanningTable' })
  const bookings = bookingHooks.getDocs()
  
  const [openNew, setOpenNew] = useState(false)
  const [startDate, setStartDate] = useState(day)
  const [selectedEditBooking, selectEditBooking] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedNewFlightBooking, selectNewFlightBooking] = useState(null)
  const [openAvailability, setOpenAvailability] = useState(null)
  
  const title = useMemo(() => {
    switch (slotType) {
      case 'free':
        return resource.type === 'aircraft' ? 'RESERVER' : null
      case 'book':
        return booking.user && booking.bookingType === 'flight' ? `type de vol: ${booking.flightType?.name}\nPilote: ${booking.user?.firstname} ${booking.user?.lastname}` : null
      case 'unavailable':
        return 'Non réservable'
    }
  }, [slotType, booking])
  
  const bgcolor = useMemo(() => {
    if (slotType === 'book') {
      if (booking.bookingType === BOOKING_TYPE_MAINTENANCE || booking.bookingType === BOOKING_TYPE_RENTING) return 'grey.600'
      else if (booking.pilotRef?.id === profile.id || booking.instructorRef?.id === profile.id) return '#6EC402'
      else if (booking.bookingType === BOOKING_TYPE_ACTIVITY) return '#337AFF'
      else return '#BF3FA6'
    } else return '#f2f2f2'
  }, [slotType, booking])
  
  const cursor = useMemo(() => {
    if (adminMode) return 'pointer'
    else if (resource?.type === 'aircraft' && slotType === 'free') return 'pointer'
    else if (profile?.id === booking?.pilotRef?.id) return 'pointer'
    else if (profile?.id === resource?.id && slotType === 'free') return 'pointer'
    else if (booking?.bookingType === BOOKING_TYPE_ACTIVITY) return 'pointer'
    else return 'default'
  }, [adminMode, resource, profile, slotType, booking])
  
  const bookFlight = key => {
    setStartDate(addHours(startOfDay(day), 8 + key / 4))
    setOpenNew(true)
  }
  
  const handleClick = e => {
    if (slotType === 'free') {
      if (resource.type === 'aircraft') bookFlight(start)
      else if (adminMode || profile.id === resource.id) {
        const slotStart = addMinutes(new Date(format(day, 'yyyy-MM-dd') + 'T08:00'), 15 * start)
        const foundedAvailability = availabilities.find(a => a.startDate.getTime() <= slotStart.getTime() && a.endDate.getTime() >= slotStart.getTime())
        setOpenAvailability(foundedAvailability)
      }
    }
    if (slotType === 'book') {
      if (booking.activityRef)
        navigate(ACTIVITIES + '/' + booking.activityRef.id)
      else if (adminMode || booking.pilotRef.id === profile.id || booking.instructorRef.id === profile.id)
        setAnchorEl(e.currentTarget)
    }
  }
  
  return (
    <>
      <Box
        component='td'
        id={id}
        colSpan={length || 1}
        title={title}
        onClick={handleClick}
        sx={pickBy({
          bgcolor,
          background: slotType === 'unavailable' && `url(${hatch})`,
          borderLeft: '1px solid #FFF',
          borderColor: 'rgba(0, 0, 0, .1)',
          cursor,
          textAlign: 'center',
        })}
      >{label}</Box>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          style: {
            width: 200,
          },
        }}
      >
        <MenuItem onClick={() => {
          selectEditBooking(bookings.find(b => b.id === anchorEl.id))
          setAnchorEl(null)
        }}>
          <ListItemIcon><Create /></ListItemIcon>Modifier
        </MenuItem>
        <MenuItem onClick={() => bookingHooks.deleteDoc(anchorEl.id).then(() => setAnchorEl(null))}>
          <ListItemIcon><Delete /></ListItemIcon>Supprimer
        </MenuItem>
        {anchorEl && bookings.find(b => b.id === anchorEl.id)?.bookingType === 'flight' && <MenuItem onClick={() => {
          selectNewFlightBooking(bookings.find(b => b.id === anchorEl.id))
          setAnchorEl(null)
        }}>
          <ListItemIcon><Send /></ListItemIcon>Saisir le vol
        </MenuItem>}
      </Menu>
      
      <Dialog open={openNew} onClose={() => setOpenNew(false)} aria-labelledby='form-dialog-title'>
        <DialogTitle id='new-booking-dialog'>Nouvelle réservation</DialogTitle>
        <NewBookingForm onClose={() => setOpenNew(false)} startDateInit={startDate} aircraftInit={resource} />
      </Dialog>
      
      <Dialog open={Boolean(selectedEditBooking)} onClose={() => selectEditBooking(null)} aria-labelledby='form-dialog-title'>
        <DialogTitle id='form-dialog-title'>Modifier une réservation</DialogTitle>
        {selectedEditBooking && <EditBookingForm onClose={() => selectEditBooking(null)} booking={selectedEditBooking} />}
      </Dialog>
      
      {selectedNewFlightBooking && <NewFlightDialog open onClose={() => selectNewFlightBooking(null)} booking={selectedNewFlightBooking} />}
      {openAvailability && <WriteAvailabilityDialog open onClose={() => setOpenAvailability(null)} dayInit={day} availability={openAvailability} />}
    </>
  )
}

Slot.propTypes = {
  id: PropTypes.string,
  start: PropTypes.number,
  length: PropTypes.number,
  slotType: PropTypes.oneOf(['free', 'book', 'unavailable']).isRequired,
  resource: PropTypes.object.isRequired,
  booking: PropTypes.object,
  day: PropTypes.object,
  label: PropTypes.string,
}

export default Slot
